'use client'
import { createContext, useContext, useState, useCallback } from "react"
import { useRouter } from "next/navigation"
import { message, notification, Spin } from 'antd'
import { LoadingOutlined } from '@ant-design/icons'

import { removeAllCookies } from "@/actions/cookies"

const GlobalContext = createContext()

const GlobalProvider = ({ children }) => {
  const router = useRouter()

  const [messageApi, contextMessage] = message.useMessage()
  const [notificationApi, contextNotification] = notification.useNotification()

  const [user, setUser] = useState({})
  const [userData, setUserData] = useState({})
  const [closeDrawerMobile, setCloseDrawerMobile] = useState(false)
  const [mailRegisterSuccess, setMailRegisterSuccess] = useState('')
  const [pagesListPermissions, setPagesListPermissions] = useState({
    canShowNews: false,
    canShowTranscriptions: false,
  })

  const [modalTutorials, setModalTutorials] = useState({
    open: false
  })

  const handleLogout = useCallback(async () => {
    await removeAllCookies()
    if (typeof window !== 'undefined') {
      router.push('/')
    }
  }, [router])

  const handleMessage = useCallback((
    type,
    message,
    duration
  ) => {
    messageApi.open({
      type: type || 'info',
      content: message,
      duration: duration || 7,
    })
  }, [messageApi])

  const handleNotification = useCallback((
    type,
    title,
    message,
    duration,
    placement,
    loading,
    showProgress
  ) => {
    notificationApi.open({
      type: type || 'info',
      message: title || null,
      description: message,
      placement: placement || 'topRight',
      duration: duration || 10,
      icon: loading && <Spin indicator={<LoadingOutlined spin />} />,
      showProgress: showProgress || false
    })
  }, [notificationApi])

  return (
    <GlobalContext.Provider
      value={{
        user,
        closeDrawerMobile,
        setUser,
        userData,
        mailRegisterSuccess,
        setMailRegisterSuccess,
        setUserData,
        handleNotification,
        setCloseDrawerMobile,
        handleLogout,
        handleMessage,
        pagesListPermissions,
        modalTutorials,
        setModalTutorials
      }}
    >
      {contextMessage}
      {contextNotification}
      {children}
    </GlobalContext.Provider>
  )
}

const useGlobal = () => {
  const context = useContext(GlobalContext)

  if (!context) {
    throw new Error('useGlobal must be used within an GlobalProvider')
  }

  return context
}

export { GlobalProvider, useGlobal }